// src/lib/deliverySettings.ts
import { supabase } from './supabaseClient'; // Ajuste o caminho se o seu supabaseClient estiver em outro local

export interface DeliveryFeeConfig {
  id?: number;
  fee_type: 'fixed' | 'per_km';
  fixed_rate?: number | null;
  per_km_rate?: number | null;
  base_fee_for_per_km?: number | null;
  created_at?: string;
  updated_at?: string;
}

const DELIVERY_CONFIG_TABLE = 'delivery_fee_configuration';
const CONFIG_ID = 1; // We are using a single row with ID 1 for configuration

/**
 * Fetches the delivery fee configuration from Supabase.
 * There should only be one row with id = 1.
 */
export const fetchDeliveryFeeConfiguration = async (): Promise<DeliveryFeeConfig | null> => {
  console.log("LOG: fetchDeliveryFeeConfiguration - Iniciando busca...");
  const { data, error } = await supabase
    .from(DELIVERY_CONFIG_TABLE)
    .select('*')
    .eq('id', CONFIG_ID)
    .maybeSingle(); // Use maybeSingle() as we expect one row or null

  if (error) {
    console.error('LOG: fetchDeliveryFeeConfiguration - Erro ao buscar configuração:', error);
    throw new Error(`Erro ao buscar configuração de taxa de entrega: ${error.message}`);
  }

  if (!data) {
    console.warn("LOG: fetchDeliveryFeeConfiguration - Nenhuma configuração de taxa de entrega encontrada com id=1. Retornando null.");
    // You might want to return a default configuration or handle this case upstream
    return null;
  }
  
  console.log("LOG: fetchDeliveryFeeConfiguration - Configuração buscada:", data);
  // Ensure numeric values are numbers, as Supabase might return them as strings from NUMERIC type
  return {
    ...data,
    fixed_rate: data.fixed_rate !== null && data.fixed_rate !== undefined ? Number(data.fixed_rate) : null,
    per_km_rate: data.per_km_rate !== null && data.per_km_rate !== undefined ? Number(data.per_km_rate) : null,
    base_fee_for_per_km: data.base_fee_for_per_km !== null && data.base_fee_for_per_km !== undefined ? Number(data.base_fee_for_per_km) : null,
  } as DeliveryFeeConfig;
};

/**
 * Updates the delivery fee configuration in Supabase.
 * Assumes a row with id = 1 already exists or should be created (upsert).
 * The SQL migration includes an INSERT ON CONFLICT to ensure id=1 exists.
 */
export const updateDeliveryFeeConfiguration = async (
  config: Partial<DeliveryFeeConfig>
): Promise<DeliveryFeeConfig | null> => {
  console.log("LOG: updateDeliveryFeeConfiguration - Iniciando atualização com dados:", config);
  
  // Ensure a valid fee_type is present if being updated
  if (config.fee_type && !['fixed', 'per_km'].includes(config.fee_type)) {
    throw new Error("Tipo de taxa inválido. Deve ser 'fixed' ou 'per_km'.");
  }

  const updateData = {
    ...config,
    updated_at: new Date().toISOString(), // Ensure updated_at is always set
  };

  // Remove id from updateData if present, as it's part of the where clause
  delete updateData.id; 

  const { data, error } = await supabase
    .from(DELIVERY_CONFIG_TABLE)
    .update(updateData)
    .eq('id', CONFIG_ID)
    .select()
    .single(); // Expects a single row to be returned after update

  if (error) {
    console.error('LOG: updateDeliveryFeeConfiguration - Erro ao atualizar configuração:', error);
    // Attempt to upsert if the error indicates the row doesn't exist (though the migration should prevent this)
    if (error.code === 'PGRST204') { // PostgREST error for "No rows found"
        console.warn("LOG: updateDeliveryFeeConfiguration - Linha de configuração não encontrada para update, tentando criar (upsert)...");
        const { data: upsertData, error: upsertError } = await supabase
            .from(DELIVERY_CONFIG_TABLE)
            .upsert({ ...config, id: CONFIG_ID, updated_at: new Date().toISOString() }, { onConflict: 'id' })
            .select()
            .single();
        if (upsertError) {
            console.error('LOG: updateDeliveryFeeConfiguration - Erro no upsert:', upsertError);
            throw new Error(`Erro ao criar/atualizar configuração de taxa de entrega: ${upsertError.message}`);
        }
        console.log("LOG: updateDeliveryFeeConfiguration - Configuração criada via upsert:", upsertData);
        return upsertData as DeliveryFeeConfig;
    }
    throw new Error(`Erro ao atualizar configuração de taxa de entrega: ${error.message}`);
  }

  console.log("LOG: updateDeliveryFeeConfiguration - Configuração atualizada:", data);
  return {
    ...data,
    fixed_rate: data.fixed_rate !== null && data.fixed_rate !== undefined ? Number(data.fixed_rate) : null,
    per_km_rate: data.per_km_rate !== null && data.per_km_rate !== undefined ? Number(data.per_km_rate) : null,
    base_fee_for_per_km: data.base_fee_for_per_km !== null && data.base_fee_for_per_km !== undefined ? Number(data.base_fee_for_per_km) : null,
  } as DeliveryFeeConfig;
};

// Example of how to get the Supabase client if it's not globally available
// You might need to adjust this based on your project structure.
// If 'src/lib/supabaseClient.ts' exports the Supabase client instance:
/*
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Supabase URL or Anon Key is missing. Check your environment variables.");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);
*/
